import React from 'react';
import {
  LineChart, Line, AreaChart, Area,
  BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

// ── KPI Card ─────────────────────────────────────────────────
export function KPICard({ label, value, delta, deltaType, sparkData, delay = 0, error = null }) {
  const deltaColor =
    deltaType === 'up'   ? '#0f7a46' :
    deltaType === 'down' ? '#b02828' :
    deltaType === 'warn' ? '#c47a1a' : 'var(--page-text-sec)';

  const deltaBg =
    deltaType === 'up'   ? 'rgba(15,122,70,0.1)'  :
    deltaType === 'down' ? 'rgba(176,40,40,0.1)'  :
    deltaType === 'warn' ? 'rgba(196,122,26,0.1)' : 'rgba(0,0,0,0.04)';

  const deltaPrefix =
    deltaType === 'up'   ? '↑' :
    deltaType === 'down' ? '↓' :
    deltaType === 'warn' ? '⚠' : '';

  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--card-border)',
        borderRadius: 8,
        padding: '16px 18px 14px',
        position: 'relative', overflow: 'hidden',
        cursor: 'default',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        animation: `fadeUp 0.5s ease ${delay}s both`,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'rgba(200,149,32,0.35)';
        e.currentTarget.style.boxShadow   = '0 2px 16px rgba(0,0,0,0.06)';
        const accent = e.currentTarget.querySelector('.kpi-accent');
        if (accent) accent.style.opacity = '1';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--card-border)';
        e.currentTarget.style.boxShadow   = 'none';
        const accent = e.currentTarget.querySelector('.kpi-accent');
        if (accent) accent.style.opacity = '0';
      }}
    >
      {/* Gold top accent on hover */}
      <div className="kpi-accent" style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: 'linear-gradient(90deg, var(--gold), var(--gold-light))',
        opacity: 0, transition: 'opacity 0.2s',
      }} />

      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 8.5, letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--page-muted)', marginBottom: 10,
      }}>{label}</div>

      {/* Inter 800 for the value — the hero number */}
      <div style={{
        fontFamily: 'var(--font-kpi)',
        fontSize: 26, fontWeight: 800,
        color: 'var(--page-text)',
        lineHeight: 1, letterSpacing: '-0.03em',
        marginBottom: 10,
      }}>{value}</div>

      {delta && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontFamily: 'var(--font-mono)', fontSize: 9.5,
          padding: '3px 7px', borderRadius: 3,
          background: deltaBg, color: deltaColor,
        }}>
          {deltaPrefix} {delta}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 9, color: '#b02828' }}>
          {error}
        </div>
      )}

      {/* Sparkline */}
      {sparkData && (
        <div style={{
          position: 'absolute', bottom: 0, right: 0,
          width: 80, height: 36, opacity: 0.18,
          pointerEvents: 'none',
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkData}>
              <Line type="monotone" dataKey="v" stroke="var(--gold)" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ── Section Header ────────────────────────────────────────────
export function SectionHeader({ title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 9, letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color: 'var(--page-muted)', whiteSpace: 'nowrap',
      }}>{title}</div>
      <div style={{ flex: 1, height: 1, background: 'var(--card-border)' }} />
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────
export function Card({ children, style = {}, delay = 0 }) {
  return (
    <div style={{
      background: 'var(--card)',
      border: '1px solid var(--card-border)',
      borderRadius: 8,
      padding: 20,
      animation: `fadeUp 0.5s ease ${delay}s both`,
      ...style,
    }}>
      {children}
    </div>
  );
}

export function CardTitle({ children }) {
  return (
    <div style={{
      fontFamily: 'var(--font-body)',
      fontSize: 13, fontWeight: 600,
      color: 'var(--page-text)',
      marginBottom: 14,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>{children}</div>
  );
}

// ── Flag / Badge ──────────────────────────────────────────────
export function Flag({ type, children }) {
  const styles = {
    critical: { bg: 'rgba(176,40,40,0.1)',   border: 'rgba(176,40,40,0.2)',   color: '#b02828' },
    warning:  { bg: 'rgba(196,122,26,0.1)',  border: 'rgba(196,122,26,0.2)',  color: '#c47a1a' },
    ok:       { bg: 'rgba(15,122,70,0.1)',   border: 'rgba(15,122,70,0.2)',   color: '#0f7a46' },
    info:     { bg: 'rgba(37,99,235,0.08)',  border: 'rgba(37,99,235,0.18)',  color: '#2563eb' },
  };
  const s = styles[type] || styles.info;
  return (
    <span style={{
      fontFamily: 'var(--font-mono)',
      fontSize: 8.5, padding: '3px 8px',
      borderRadius: 3, letterSpacing: '0.1em',
      textTransform: 'uppercase',
      background: s.bg, border: `1px solid ${s.border}`,
      color: s.color,
    }}>{children}</span>
  );
}

// ── Revenue Trend Chart ───────────────────────────────────────
export function RevenueTrendChart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={180}>
      <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#c89520" stopOpacity={0.15} />
            <stop offset="95%" stopColor="#c89520" stopOpacity={0}    />
          </linearGradient>
          <linearGradient id="gpGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#0f7a46" stopOpacity={0.12} />
            <stop offset="95%" stopColor="#0f7a46" stopOpacity={0}    />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e4e6ec" />
        <XAxis dataKey="label"
          tick={{ fill: '#9898b0', fontSize: 9, fontFamily: 'DM Mono' }}
          axisLine={false} tickLine={false} />
        <YAxis
          tick={{ fill: '#9898b0', fontSize: 9, fontFamily: 'DM Mono' }}
          axisLine={false} tickLine={false}
          tickFormatter={v => `R${(v/1000000).toFixed(1)}M`} />
        <Tooltip
          contentStyle={{ background: '#ffffff', border: '1px solid #e4e6ec', borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
          labelStyle={{ color: '#5a5a72', fontFamily: 'DM Mono', fontSize: 10 }}
          itemStyle={{ color: '#1a1a2e', fontFamily: 'DM Mono', fontSize: 10 }}
          formatter={v => [`R${v.toLocaleString('en-ZA')}`, '']}
        />
        <Legend wrapperStyle={{ fontFamily: 'DM Mono', fontSize: 10, color: '#9898b0' }} />
        <Area type="monotone" dataKey="revenue" name="Revenue"
          stroke="#c89520" strokeWidth={2} fill="url(#revGrad)"
          dot={{ r: 3, fill: '#c89520' }} />
        <Area type="monotone" dataKey="grossProfit" name="Gross Profit"
          stroke="#0f7a46" strokeWidth={1.5} fill="url(#gpGrad)"
          strokeDasharray="4 3" dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ── Branch Bar Chart ──────────────────────────────────────────
export function BranchBarChart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} layout="vertical" margin={{ left: 0, right: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e4e6ec" horizontal={false} />
        <XAxis type="number"
          tick={{ fill: '#9898b0', fontSize: 9, fontFamily: 'DM Mono' }}
          axisLine={false} tickLine={false}
          tickFormatter={v => `R${(v/1000000).toFixed(1)}M`} />
        <YAxis dataKey="StoreName" type="category"
          tick={{ fill: '#5a5a72', fontSize: 10, fontFamily: 'Instrument Sans' }}
          axisLine={false} tickLine={false} width={80} />
        <Tooltip
          contentStyle={{ background: '#ffffff', border: '1px solid #e4e6ec', borderRadius: 6 }}
          labelStyle={{ color: '#5a5a72', fontFamily: 'DM Mono', fontSize: 10 }}
          itemStyle={{ color: '#1a1a2e', fontFamily: 'DM Mono', fontSize: 10 }}
          formatter={v => [`R${v.toLocaleString('en-ZA')}`, '']}
        />
        <Bar dataKey="Revenue" name="Revenue" fill="#c89520" radius={[0, 3, 3, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Metric Row ────────────────────────────────────────────────
export function MetricRow({ label, value, valueColor, last = false }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '7px 0',
      borderBottom: last ? 'none' : '1px solid #f0f1f6',
    }}>
      <span style={{ fontSize: 12, color: 'var(--page-text-sec)' }}>{label}</span>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 11.5,
        color: valueColor || 'var(--page-text)', fontWeight: 500,
      }}>{value}</span>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────
export function Skeleton({ height = 20, width = '100%', style = {} }) {
  return (
    <div style={{
      height, width,
      background: '#f0f1f6',
      borderRadius: 4,
      animation: 'pulse 1.5s infinite',
      ...style,
    }} />
  );
}

// ── Power BI embed wrapper ────────────────────────────────────
export function PowerBIEmbed({ embedConfig }) {
  const [status, setStatus] = React.useState('loading');
  const [retryCount, setRetryCount] = React.useState(0);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!embedConfig || !window.powerbi) return;

    if (embedConfig.error === 'CAPACITY_WARMING') {
      setStatus('warming');
      const t = setTimeout(() => {
        setRetryCount(c => c + 1);
        setStatus('loading');
      }, 30000);
      return () => clearTimeout(t);
    }

    try {
      const config = {
        type:        'report',
        id:          embedConfig.reportId,
        embedUrl:    embedConfig.embedUrl,
        accessToken: embedConfig.accessToken,
        tokenType:   window.models.TokenType.Embed,
        settings: {
          navContentPaneEnabled: false,
          filterPaneEnabled:     false,
          background:            window.models.BackgroundType.Transparent,
        },
      };
      window.powerbi.embed(containerRef.current, config);
      setStatus('ready');
    } catch (err) {
      console.error('Power BI embed error:', err);
      setStatus('error');
    }
  }, [embedConfig, retryCount]);

  if (status === 'warming') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        height: 400, gap: 16,
        background: 'var(--card)', border: '1px solid var(--card-border)',
        borderRadius: 8,
      }}>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--page-text)' }}>
          Dashboard Starting Up
        </div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', textAlign: 'center', lineHeight: 1.6 }}>
          Analytics capacity is warming up.<br />
          This takes 2–3 minutes. Retrying automatically...
        </div>
        <div style={{ width: 48, height: 2, background: 'var(--gold)', animation: 'pulse 1.5s infinite' }} />
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%', height: 500, background: 'transparent' }} />
  );
}
